import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../i18/config";
import { cancelWorkflowExecution, getWorkflowExecution } from "../../api";
import * as route from "../../api/routes";
import BreadCrumb from "../../components/Common/BreadCrumb";
import Loader from "../../components/Loader";
import { ExecutionStep, WorkflowExecution } from "./types";
import { ExecutionStatusBadge } from "./StatusBadge";
import { formatWorkflowDate, isFinalExecution } from "./format";
import { useModuleCatalog } from "./useModuleCatalog";
import { moduleColor, moduleIcon } from "./moduleStyle";
import JsonView from "./JsonView";

const STEP_CLASS: Record<ExecutionStep['status'], string> = {
    done: 'text-success',
    handled: 'text-warning',
    error: 'text-danger',
    waiting: 'text-primary',
    running: 'text-info',
};

/** Automation → Runs → one run: every operation with its input and output. */
const RunDetail = () => {
    const { t } = useTranslation(['workflow', 'menu']);
    const { uuid } = useParams();
    const catalog = useModuleCatalog();
    const [execution, setExecution] = useState<WorkflowExecution | null>(null);
    const [open, setOpen] = useState<string | null>(null);

    const load = useCallback(() => {
        getWorkflowExecution(uuid as string).then((r: any) => setExecution(r.item)).catch(() => toast.error(t('workflow:notFound')));
    }, [uuid, t]);

    useEffect(() => load(), [load]);

    useEffect(() => {
        if (!execution || isFinalExecution(execution.status)) {
            return;
        }
        const timer = setTimeout(load, 3000);

        return () => clearTimeout(timer);
    }, [execution, load]);

    if (!execution) {
        return <Loader />;
    }

    const cancel = () => cancelWorkflowExecution(execution.uuid).then(load).catch((e: any) => toast.error(e?.message || t('workflow:actionFailed')));

    return (
        <>
            <BreadCrumb title={execution.scenario?.name || ''} pageTitle={t('menu:automationRuns')} />
            <Row>
                <Col xl={4}>
                    <Card>
                        <CardHeader className="d-flex align-items-center gap-2">
                            <ExecutionStatusBadge status={execution.status} />
                            <span className="small text-muted">v{execution.scenarioVersion}</span>
                            <Link className="btn btn-sm btn-soft-primary ms-auto" to={route.AUTOMATION_SCENARIO.replace(':uuid', execution.scenario?.uuid || '') + '?run=' + execution.uuid}>
                                <i className="ri-flow-chart me-1" />{t('workflow:showOnCanvas')}
                            </Link>
                        </CardHeader>
                        <CardBody>
                            <dl className="row small mb-0">
                                <dt className="col-5">{t('workflow:startedBy')}</dt><dd className="col-7">{t('workflow:origin_' + (execution.origin?.type || 'other'), { defaultValue: execution.origin?.type })}</dd>
                                <dt className="col-5">{t('workflow:started')}</dt><dd className="col-7">{formatWorkflowDate(execution.startedAt)}</dd>
                                <dt className="col-5">{t('workflow:finished')}</dt><dd className="col-7">{formatWorkflowDate(execution.finishedAt)}</dd>
                                <dt className="col-5">{t('workflow:operations')}</dt><dd className="col-7">{execution.operations}</dd>
                                {execution.waitingFor && <><dt className="col-5">{t('workflow:waitingForColumn')}</dt><dd className="col-7">{t('workflow:waitingFor_' + (execution.waitingFor.type || 'other'), { defaultValue: execution.waitingFor.type })}{execution.waitingFor.until ? ' · ' + formatWorkflowDate(execution.waitingFor.until) : ''}</dd></>}
                            </dl>
                            {execution.error && <div className="alert alert-danger mt-3 mb-0 small"><strong>{execution.error.type}</strong>{execution.error.moduleId ? ` (${t('workflow:module')} ${execution.error.moduleId})` : ''}: {execution.error.message}</div>}
                            {!isFinalExecution(execution.status) && execution.actions?.canEdit &&
                                <button type="button" className="btn btn-sm btn-soft-danger mt-3" onClick={cancel}><i className="ri-stop-circle-line me-1" />{t('workflow:cancelRun')}</button>
                            }
                        </CardBody>
                    </Card>
                    {execution.triggerPayload &&
                        <Card>
                            <CardHeader><h6 className="mb-0">{t('workflow:triggerPayload')}</h6></CardHeader>
                            <CardBody><JsonView value={execution.triggerPayload} /></CardBody>
                        </Card>
                    }
                    {execution.variables && Object.keys(execution.variables).length > 0 &&
                        <Card>
                            <CardHeader><h6 className="mb-0">{t('workflow:variables')}</h6></CardHeader>
                            <CardBody><JsonView value={execution.variables} /></CardBody>
                        </Card>
                    }
                </Col>
                <Col xl={8}>
                    <Card>
                        <CardHeader><h6 className="mb-0">{t('workflow:operationsLog')}</h6></CardHeader>
                        <CardBody className="p-0">
                            <ul className="list-group list-group-flush">
                                {(execution.steps || []).map((step, index) => {
                                    const definition = catalog?.byKey[step.moduleKey];

                                    return (
                                        <li key={step.uuid} className="list-group-item">
                                            <div role="button" className="d-flex align-items-center gap-2" onClick={() => setOpen(open === step.uuid ? null : step.uuid)}>
                                                <span className="text-muted small" style={{ width: 28 }}>{index + 1}</span>
                                                <span className="rounded-circle d-inline-flex align-items-center justify-content-center text-white flex-shrink-0" style={{ width: 28, height: 28, background: moduleColor(step.moduleKey) }}>
                                                    <i className={moduleIcon(definition?.icon)} />
                                                </span>
                                                <span className="fw-semibold">{step.moduleId}. {definition?.label || step.moduleKey}</span>
                                                <span className={"small " + STEP_CLASS[step.status]}>{t('workflow:stepStatus_' + step.status, { defaultValue: step.status })}</span>
                                                {step.attempt > 1 && <span className="badge bg-light text-body">{t('workflow:attempt')} {step.attempt}</span>}
                                                <span className="small text-muted ms-auto">{(step.output || []).length} {t('workflow:bundles')}</span>
                                                <i className={open === step.uuid ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                                            </div>
                                            {step.error && <div className="small text-danger mt-1 ms-5">{step.error.type}: {step.error.message}</div>}
                                            {open === step.uuid &&
                                                <Row className="mt-2">
                                                    <Col md={6}><h6 className="small text-uppercase text-muted">{t('workflow:input')}</h6><JsonView value={step.input} /></Col>
                                                    <Col md={6}><h6 className="small text-uppercase text-muted">{t('workflow:output')}</h6><JsonView value={step.output} /></Col>
                                                </Row>
                                            }
                                        </li>
                                    );
                                })}
                                {(execution.steps || []).length === 0 && <li className="list-group-item text-muted">{t('workflow:noOperations')}</li>}
                            </ul>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default RunDetail;
